import c from "../plan-template/Sidebar.module.scss";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import Sidebar from "../../components/sidebar";
import ChapterItem from "./ChapterItem";
import "overlayscrollbars/overlayscrollbars.css";
import CollapseContainerContext from "../../context/collapse-container.context";
import { usePlanContentPresetsCompileStore } from "../../store/plan-content-presets-compile.store";
import debounce from "lodash.debounce";
import EmptyBox from "../emptystate/EmptyBox";
import FilterDropdown from "../utils/FilterDropdown";
import FilterDateRange from "./FilterDateRange";
import { searchTemplatesPlansContentPresetEditor } from "../../services/plans-template-content-preset-editor.service";

const initialFilters = {
	templateTo: {
		date_from: "",
		date_to: "",
	},
	templateFrom: {
		date_from: "",
		date_to: "",
	},
	statusIds: [],
	organizationFrom: {
		date_from: "",
		date_to: "",
	},
	organizationTo: {
		date_from: "",
		date_to: "",
	},
	organization_chapter_contents: {
		statuses: [],
	},
};

const ORGANIZATION_STATUS_OPTIONS = [
	{ label: "Tutti", value: "" },
	{ label: "Completati", value: "completed" },
	{ label: "Non completati", value: "uncompleted" },
];

function SidebarPage({ isAdmin, planData }) {
	const {
		chapterList,
		searchModel,
		searchLoading,
		searchResults,
		planDetail,
		updateState,
		isFiltering,
	} = usePlanContentPresetsCompileStore();

	const { forceOpen, forceClose } = useContext(CollapseContainerContext);

	const abortController = useRef(new AbortController());
	const isInit = useRef(null);

	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const [filters, setFilters] = useState({ ...initialFilters });

	function searchChapter(e) {
		const search = e.target.value;

		if (search.trim().length > 2) {
			filterContents({
				search: search.trim().toLowerCase(),
				organizationFrom: filters.organizationFrom,
				organizationTo: filters.organizationTo,
				templateFrom: filters.templateFrom,
				templateTo: filters.templateTo,
				statusIds: filters.statusIds,
				withTodo: filters.with_todo,
				organization_chapter_contents: filters.organization_chapter_contents,
			});
		} else {
			updateState({ searchModel: "" });
		}
	}

	const filterContents = ({
		search,
		organizationFrom,
		organizationTo,
		templateFrom,
		templateTo,
		statusIds,
		organization_chapter_contents,
	}) => {
		updateState({ searchLoading: true });

		abortController.current.abort();
		abortController.current = new AbortController();

		const body = {
			templatePlan: planData.template_plan_id,
			contentPreset: planData.content_preset_id,
			id: planDetail.organization_id,
			idPlan: planDetail.id,
			q: search,
			template_requirements: {
				start_at: templateFrom,
				end_at: templateTo,
			},
			organization_requirements: {
				start_at: organizationFrom,
				end_at: organizationTo,
				requirement_status_ids: statusIds,
			},
			...(organization_chapter_contents.statuses.length && {
				organization_chapter_contents: organization_chapter_contents,
			}),
		};

		searchTemplatesPlansContentPresetEditor(
			{ params: body, options: { signal: abortController.current.signal } },
			(res) => {
				updateState({
					searchLoading: false,
					searchModel: search.trim().toLowerCase(),
					searchResults: res,
				});
			},
			(err) => {
				updateState({ searchLoading: false });
			}
		);
	};

	const debounceSearch = debounce(searchChapter, 500);

	useEffect(() => {
		if (!isInit.current) {
			isInit.current = true;
			return;
		}

		filterContents({
			search: searchModel,
			organizationFrom: filters.organizationFrom,
			organizationTo: filters.organizationTo,
			templateFrom: filters.templateFrom,
			templateTo: filters.templateTo,
			statusIds: filters.statusIds,
			organization_chapter_contents: filters.organization_chapter_contents,
		});
	}, [JSON.stringify(filters)]);

	const applyTemplateDateHandler = (templateDate) => {
		setFilters((f) => ({
			...f,
			templateFrom: {
				date_from: templateDate.fromStart,
				date_to: templateDate.fromEnd,
			},
			templateTo: {
				date_from: templateDate.toStart,
				date_to: templateDate.toEnd,
			},
		}));
	};

	const applyOrganizationDateHandler = (date) => {
		setFilters((f) => ({
			...f,
			organizationFrom: {
				date_from: date.fromStart,
				date_to: date.fromEnd,
			},
			organizationTo: {
				date_from: date.toStart,
				date_to: date.toEnd,
			},
		}));
	};

	const resetFilter = () => {
		setFilters({ ...initialFilters });
	};

	const counterTemplateDate =
		(filters.templateTo.date_to || filters.templateTo.date_from ? 1 : 0) +
		(filters.templateFrom.date_to || filters.templateFrom.date_from ? 1 : 0);

	const counterOrganizationDate =
		(filters.organizationTo.date_to || filters.organizationTo.date_from ? 1 : 0) +
		(filters.organizationFrom.date_to || filters.organizationFrom.date_from ? 1 : 0);

	const hasFilterApplied = JSON.stringify(filters) !== JSON.stringify(initialFilters);

	const filteredChapters = chapterList.filter(
		(chapter) => searchResults.organization_chapters.includes(chapter.content_id) || !isFiltering
	);

	useEffect(() => {
		updateState({ isFiltering: !!searchModel || hasFilterApplied });
	}, [JSON.stringify(filters), searchModel]);

	return (
		<Sidebar hasFilterApplied={hasFilterApplied}>
			<Sidebar.Header>
				<div className="flex items-center gap-4">
					<Sidebar.ActionButton
						onClick={forceOpen}
						dataAttr={{
							"data-tooltip-id": "tooltip_small_top",
							"data-tooltip-offset": 10,
							"data-tooltip-position-strategy": "fixed",
							"data-tooltip-content": "Espandi tutti",
						}}
						iconClass={"expand"}
					/>

					<Sidebar.ActionButton
						onClick={forceClose}
						dataAttr={{
							"data-tooltip-id": "tooltip_small_top",
							"data-tooltip-offset": 10,
							"data-tooltip-position-strategy": "fixed",
							"data-tooltip-content": "Riduci tutti",
						}}
						iconClass={"collapsed"}
					/>
					<div className="wrap_input_landing icon w-full">
						<input
							onChange={debounceSearch}
							type="text"
							className="landing"
							placeholder="Ricerca..."
						/>
						<span className="icon_16 default search"></span>
					</div>

					<Sidebar.FilterToggle />
				</div>

				<Sidebar.FilterContent title="Filtra contenuti del piano" onReset={() => resetFilter()}>
					<FilterDropdown
						options={ORGANIZATION_STATUS_OPTIONS}
						type={"radio"}
						activeSelected={filters.organization_chapter_contents.statuses[0] || ""}
						onApply={(item) => {
							setFilters((f) => ({
								...f,
								organization_chapter_contents: {
									statuses: item ? [item] : [],
								},
							}));
						}}
						toggleLabel="Stato documenti"
					/>

					<FilterDropdown
						options={planData.organization_requirements_statuses.map((status) => ({
							label: status.name,
							value: status.id,
							icon: `icon_16 status ${status.icon}`,
						}))}
						activeSelected={filters.statusIds}
						onApply={(items) => setFilters((f) => ({ ...f, statusIds: items }))}
						toggleLabel="Stato linee d'azione"
					/>

					<FilterDateRange
						label={"Scadenze piano"}
						currentValue={{
							fromStart: filters.templateFrom?.date_from,
							fromEnd: filters.templateFrom?.date_to,
							toStart: filters.templateTo?.date_from,
							toEnd: filters.templateTo?.date_to,
						}}
						onApply={applyTemplateDateHandler}
						counter={counterTemplateDate}
					/>

					<FilterDateRange
						label={"Scadenze ente"}
						currentValue={{
							fromStart: filters.organizationFrom?.date_from,
							fromEnd: filters.organizationFrom?.date_to,
							toStart: filters.organizationTo?.date_from,
							toEnd: filters.organizationTo?.date_to,
						}}
						onApply={applyOrganizationDateHandler}
						counter={counterOrganizationDate}
					/>
				</Sidebar.FilterContent>
			</Sidebar.Header>
			<Sidebar.List
				scrollbarProps={{
					defer: true,
					options: { scrollbars: { autoHide: "scroll" } },
				}}
				className={"bg-white"}
			>
				<div className={c.main_content_sidebar}>
					{filteredChapters.length > 0 ? (
						<div className={c.main_list_sidebar}>
							<div className={c.wrap_list_sidebar}>
								{filteredChapters.map((chapter, index) => (
									<Fragment key={chapter.content_id}>
										<ChapterItem position={index} chapter={chapter} />
									</Fragment>
								))}
							</div>
						</div>
					) : (
						<div className="py-6">
							<EmptyBox title={"Nessun risultato trovato"} icon={"icon_75 default archive_plan"} />
						</div>
					)}
				</div>
				{searchLoading && (
					<div
						className={
							"absolute w-full h-full bg-white/70 flex justify-center items-center backdrop-blur-sm"
						}
					>
						<div className="main_comp_loader_box_icon !bg-dark-70"></div>
					</div>
				)}
			</Sidebar.List>
		</Sidebar>
	);
}

export default SidebarPage;
